'use client'

import React, { useState, useEffect, useCallback } from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { X } from 'lucide-react'
// Supabase identity (2026-08-26): the Clerk shim this used reported everyone signed
// out, so member prefill was dead since the cutover. Name comes from the auth
// metadata; company from the member's profile row.
import { useSupabaseAuth } from '@/context/supabaseContext'
import { supabaseBrowser } from '@/lib/supabase/client'
import { useForm } from 'react-hook-form'
import { usePathname } from 'next/navigation'
import { Button } from '@/ui/atoms/Button'
import { Icon } from '@/ui/atoms/Icon'
import { Spinner } from '@/ui/atoms/Spinner'
import { useToast } from '@/ui/components/Toast/use-toast'
import { trackEvent } from '@/lib/analytics/posthog'
import { cn } from '@/utils/ui'
import { getClientSideURL } from '@/utils/getURL'

interface ExpertGuidanceFormData {
  name: string
  company: string
  email: string
  phone: string
  valueChainArea: string
  description: string
  contactMethod: string
}

interface ExpertGuidanceModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  sourcePageType?: string
  sourceCtaPlacement?: string
}

const VALUE_CHAIN_AREAS = [
  'Battery Cell Manufacturing Process',
  'Battery Module Manufacturing Process',
  'Battery Module Development Process',
  'Battery Recycling & Reuse Process',
  'Battery Safety',
  'EV Battery Maintenance & Repair',
  'Safe Battery Handling, Storage & Transportation',
  'Other',
]

export function ExpertGuidanceModal({
  open,
  onOpenChange,
  sourcePageType,
  sourceCtaPlacement,
}: ExpertGuidanceModalProps) {
  const { user } = useSupabaseAuth()
  const pathname = usePathname()
  const { toast } = useToast()
  const [isSubmitting, setIsSubmitting] = useState(false)

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ExpertGuidanceFormData>({
    defaultValues: {
      name: '',
      company: '',
      email: '',
      phone: '',
      valueChainArea: '',
      description: '',
      contactMethod: 'email',
    },
  })

  // Pre-fill for members: name/email from the session, company from their profile
  useEffect(() => {
    if (!user) return
    let cancelled = false
    const name =
      (user.user_metadata?.full_name as string | undefined) ||
      (user.user_metadata?.name as string | undefined) ||
      ''
    supabaseBrowser()
      .from('profiles')
      .select('display_name,company')
      .eq('id', user.id)
      .maybeSingle()
      .then(({ data }) => {
        if (cancelled) return
        reset({
          name: data?.display_name || name,
          company: data?.company || '',
          email: user.email || '',
          phone: '',
          valueChainArea: '',
          description: '',
          contactMethod: 'email',
        })
      })
    return () => {
      cancelled = true
    }
  }, [user, reset])

  // Track modal open
  useEffect(() => {
    if (open) {
      trackEvent('expert_guidance_opened', {
        source_page_type: sourcePageType,
        source_cta_placement: sourceCtaPlacement,
        source_cta_type: 'button',
        page_path: pathname,
      })
    }
  }, [open, sourcePageType, sourceCtaPlacement, pathname])

  const onSubmit = useCallback(
    async (data: ExpertGuidanceFormData) => {
      setIsSubmitting(true)
      try {
        const res = await fetch(`${getClientSideURL()}/api/expert-guidance`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...data,
            pagePath: pathname,
            sourcePageType,
            sourceCtaPlacement,
          }),
        })

        if (!res.ok) {
          throw new Error('Failed to submit')
        }

        trackEvent('expert_guidance_submitted', {
          value_chain_area: data.valueChainArea,
          has_description: !!data.description,
          contact_method: data.contactMethod,
          source_page_type: sourcePageType,
          source_cta_placement: sourceCtaPlacement,
          page_path: pathname,
        })

        toast({
          icon: 'chat-3-line',
          title: 'Request submitted!',
          description: 'Our battery expert will get back to you within 24 hours.',
          duration: 7000,
        })

        onOpenChange(false)
        reset()
      } catch {
        toast({
          title: 'Something went wrong',
          description: 'Please try again or contact us directly.',
          duration: 5000,
        })
      } finally {
        setIsSubmitting(false)
      }
    },
    [pathname, sourcePageType, sourceCtaPlacement, toast, onOpenChange, reset],
  )

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-bg-intense-transparent-20 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          className={cn(
            'fixed top-0 right-0 z-50 h-full w-full sm:w-[480px] bg-bg-subtle shadow-lg',
            'flex flex-col',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=open]:slide-in-from-right data-[state=closed]:slide-out-to-right',
            'duration-300',
          )}
        >
          {/* Header */}
          <div className="flex items-start justify-between p-6 pb-0">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-10 h-10 rounded-full bg-blue-20">
                <Icon name="user-3-line" className="!w-5 !h-5 text-white" />
              </div>
              <DialogPrimitive.Title className="text-headline-S-semibold text-text-base">
                Talk to a Battery Expert
              </DialogPrimitive.Title>
            </div>
            <DialogPrimitive.Close className="flex items-center justify-center w-8 h-8 rounded-sm border border-border-input bg-bg-base hover:opacity-80 transition-opacity">
              <X className="h-4 w-4 text-text-base" />
              <span className="sr-only">Close</span>
            </DialogPrimitive.Close>
          </div>

          {/* Subtitle */}
          <div className="px-6 py-4 border-b border-border-subtle">
            <p className="text-body-S-regular text-text-subtle">
              Our <span className="text-blue-20 font-medium">battery industry expert</span> will
              review your needs and your specific situation — free of charge.
            </p>
            <div className="flex items-center gap-2 mt-3 px-3 py-1.5 bg-bg-subtle-blue rounded-md w-fit">
              <span className="w-2 h-2 rounded-full bg-blue-20 animate-pulse" />
              <span className="text-label-XS-regular text-blue-20">
                Typically responds within 24 hours
              </span>
            </div>
          </div>

          {/* Form */}
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="flex-1 overflow-y-auto px-6 py-4"
          >
            <div className="flex flex-col gap-4">
              {/* Name + Company row */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-label-XS-semibold text-text-base mb-1.5">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    className={cn(
                      'w-full px-3 py-2.5 text-body-S-regular border rounded-sm bg-bg-base text-text-base',
                      'focus:outline-none focus:ring-1 focus:ring-blue-20 focus:border-blue-20',
                      user ? 'border-blue-80 bg-bg-subtle-blue' : 'border-border-input',
                      errors.name && 'border-red-500',
                    )}
                    {...register('name', { required: 'Name is required' })}
                  />
                  {errors.name && (
                    <p className="text-label-XS-regular text-red-500 mt-1">{errors.name.message}</p>
                  )}
                </div>
                <div>
                  <label className="block text-label-XS-semibold text-text-base mb-1.5">
                    Company <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    className={cn(
                      'w-full px-3 py-2.5 text-body-S-regular border rounded-sm bg-bg-base text-text-base',
                      'focus:outline-none focus:ring-1 focus:ring-blue-20 focus:border-blue-20',
                      user ? 'border-blue-80 bg-bg-subtle-blue' : 'border-border-input',
                      errors.company && 'border-red-500',
                    )}
                    {...register('company', { required: 'Company is required' })}
                  />
                  {errors.company && (
                    <p className="text-label-XS-regular text-red-500 mt-1">
                      {errors.company.message}
                    </p>
                  )}
                </div>
              </div>

              {/* Email */}
              <div>
                <label className="block text-label-XS-semibold text-text-base mb-1.5">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  className={cn(
                    'w-full px-3 py-2.5 text-body-S-regular border rounded-sm bg-bg-base text-text-base',
                    'focus:outline-none focus:ring-1 focus:ring-blue-20 focus:border-blue-20',
                    user ? 'border-blue-80 bg-bg-subtle-blue' : 'border-border-input',
                    errors.email && 'border-red-500',
                  )}
                  {...register('email', {
                    required: 'Email is required',
                    pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Invalid email' },
                  })}
                />
                {errors.email && (
                  <p className="text-label-XS-regular text-red-500 mt-1">{errors.email.message}</p>
                )}
              </div>

              {/* Phone (optional) */}
              <div>
                <label className="block text-label-XS-semibold text-text-base mb-1.5">
                  Phone <span className="text-text-faded text-label-XS-regular">(optional)</span>
                </label>
                <input
                  type="tel"
                  className={cn(
                    'w-full px-3 py-2.5 text-body-S-regular border border-border-input rounded-sm bg-bg-base text-text-base',
                    'focus:outline-none focus:ring-1 focus:ring-blue-20 focus:border-blue-20',
                  )}
                  {...register('phone')}
                />
              </div>

              {/* Value Chain Area */}
              <div>
                <label className="block text-label-XS-semibold text-text-base mb-1.5">
                  Value Chain Area <span className="text-red-500">*</span>
                </label>
                <select
                  className={cn(
                    'w-full px-3 py-2.5 text-body-S-regular border border-border-input rounded-sm bg-bg-base text-text-base',
                    'focus:outline-none focus:ring-1 focus:ring-blue-20 focus:border-blue-20',
                    errors.valueChainArea && 'border-red-500',
                  )}
                  {...register('valueChainArea', { required: 'Value chain area is required' })}
                >
                  <option value="">Select...</option>
                  {VALUE_CHAIN_AREAS.map((area) => (
                    <option key={area} value={area}>
                      {area}
                    </option>
                  ))}
                </select>
                {errors.valueChainArea && (
                  <p className="text-label-XS-regular text-red-500 mt-1">
                    {errors.valueChainArea.message}
                  </p>
                )}
              </div>

              {/* Description (optional) */}
              <div>
                <label className="block text-label-XS-semibold text-text-base mb-1.5">
                  Tell us about your needs{' '}
                  <span className="text-text-faded text-label-XS-regular">(optional)</span>
                </label>
                <textarea
                  className={cn(
                    'w-full px-3 py-2.5 text-body-S-regular border border-border-input rounded-sm bg-bg-base text-text-base',
                    'focus:outline-none focus:ring-1 focus:ring-blue-20 focus:border-blue-20',
                    'min-h-[80px] resize-y',
                  )}
                  placeholder="What specific challenge are you trying to solve? Any requirements or constraints?"
                  {...register('description')}
                />
              </div>

              {/* Contact method */}
              <div>
                <label className="block text-label-XS-semibold text-text-base mb-1.5">
                  Preferred contact method
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 text-body-S-regular text-text-subtle cursor-pointer">
                    <input
                      type="radio"
                      value="email"
                      className="accent-blue-20 w-4 h-4"
                      {...register('contactMethod')}
                    />
                    Email
                  </label>
                  <label className="flex items-center gap-2 text-body-S-regular text-text-subtle cursor-pointer">
                    <input
                      type="radio"
                      value="phone"
                      className="accent-blue-20 w-4 h-4"
                      {...register('contactMethod')}
                    />
                    Phone
                  </label>
                  <label className="flex items-center gap-2 text-body-S-regular text-text-subtle cursor-pointer">
                    <input
                      type="radio"
                      value="either"
                      className="accent-blue-20 w-4 h-4"
                      {...register('contactMethod')}
                    />
                    Either
                  </label>
                </div>
              </div>
            </div>
          </form>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-border-subtle">
            <Button
              type="submit"
              variant="primary"
              size="lg"
              className="w-full"
              disabled={isSubmitting}
              onClick={handleSubmit(onSubmit)}
            >
              {isSubmitting ? (
                <span className="flex items-center gap-2">
                  <Spinner /> Submitting...
                </span>
              ) : (
                'Submit Request'
              )}
            </Button>
            <p className="text-label-XS-regular text-text-faded text-center mt-2">
              Your information is only shared with our expert for matchmaking purposes.
            </p>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}
